import { Container, Text } from "pixi.js"

import { BUTTON_SIZES } from "../ui_tokens"
import { DISPLAY_FONT_FAMILY, TextRole, trackLetters } from "../../text/typography"
import { createIconButton } from "../button_factory"
import { UiButton } from "../ui_button"
import { TextRenderer } from "../../text/text_renderer"
import { TEXT, TEXT_DIM } from "./theme"

// Text helpers the character-creation page sets its words with, and the pill buttons, rotation pills
// and pager the in-game customizer shares.

export const text = (
    value: string,
    {
        role = "compactLabel",
        color = TEXT,
        bold = false,
        wrap,
        display = false,
        weight,
        size,
    }: {
        role?: TextRole
        color?: number
        bold?: boolean
        wrap?: number
        // The display face (Jost) -- headings and the story's prose, so this screen speaks in the
        // same voice as the loading screen and the login card before it. Labels and chips stay in
        // the UI face.
        display?: boolean
        // A numeric weight for the display face ("400", "500"); `bold` is the UI face's on/off.
        weight?: string
        size?: number
    } = {},
): Text => {
    // No black outline. Every label on this screen sits on a flat panel or chip, so the stroke buys
    // nothing -- and on the light fills (a primary pill, a selected chip, a numbered badge) it wraps
    // the dark ink in a halo of its own colour, which smears small bold type into a blur.
    const renderer = new TextRenderer().color(color).role(role).bold(bold).strokeThickness(0)
    if (display) {
        renderer.fontFamily(DISPLAY_FONT_FAMILY)
    }
    if (size !== undefined) {
        renderer.fontSize(size)
    }
    if (wrap !== undefined) {
        // Set at build time: poking style.wordWrap afterwards re-rasterizes the Text.
        renderer.wordWrap(wrap)
    }
    const label = renderer.render(value) as Text
    if (weight) {
        // Before anything reads .width: a weight change re-rasterizes, and weights differ in advance.
        label.style.fontWeight = weight as any
    }
    return label
}

/** Letter-spaced display caps -- a heading, a kicker. See trackLetters for why not letterSpacing. */
export const capsText = (value: string, options: Parameters<typeof text>[1] = {}): Text =>
    text(trackLetters(value.toUpperCase()), { display: true, weight: "500", ...options })

// Shrink, then clip, a label to fit a box. Proportional text can't be sized from a character count,
// so measure and act on the real width.
export const fitText = (label: Text, maxWidth: number, minScale = 0.78) => {
    if (label.width <= maxWidth || label.width === 0) {
        return
    }
    const scale = Math.max(minScale, maxWidth / label.width)
    label.scale.set(scale)
    if (label.width <= maxWidth) {
        return
    }
    const source = label.text
    for (let cut = source.length - 1; cut > 1; cut--) {
        label.text = `${source.slice(0, cut).trimEnd()}…`
        if (label.width <= maxWidth) {
            return
        }
    }
}

// Footer actions share button rendering and input; the footer still owns their arrangement.
export class PillButton extends UiButton {
    readonly boxWidth: number

    constructor(props: { label: string; primary?: boolean; onClick: () => void; height?: number; leading?: { width: number } }) {
        super({ label: props.label, onClick: props.onClick, theme: "ember", size: "small",
            variant: props.primary ? "primary" : "secondary", appearance: props.primary ? "filled" : "outline",
            tone: props.primary ? "accent" : "neutral", shape: "pill", bold: !!props.primary, height: props.height ?? BUTTON_SIZES.small.height, hitPadding: { x: 4, y: 10 },
            leading: props.leading })
        this.boxWidth = this.getBoxSize().width
        this.resize(this.boxWidth, props.height ?? BUTTON_SIZES.small.height)
    }
}

/** Shared preview controls: a quarter turn per click or tap. */
export class RotationControls extends Container {
    readonly boxWidth: number

    constructor(onRotate: (step: number) => void) {
        super()
        const left = new PillButton({ label: "‹ Rotate", onClick: () => onRotate(-1) })
        const right = new PillButton({ label: "Rotate ›", onClick: () => onRotate(1) })
        right.x = left.boxWidth + 12
        this.addChild(left, right)
        this.boxWidth = right.x + right.boxWidth
    }
}

// "‹ 2 / 4 ›" for a palette too big for one page.
export class Pager extends Container {
    readonly boxWidth: number

    constructor(props: { page: number; pages: number; onPage: (page: number) => void }) {
        super()
        const { page, pages, onPage } = props

        const arrow = (glyph: string, enabled: boolean, target: number) => createIconButton({
            icon: glyph, theme: "ember", width: BUTTON_SIZES.extraSmall.height, height: BUTTON_SIZES.extraSmall.height, size: "extraSmall", enabled,
            onClick: () => onPage(target),
        })

        const left = arrow("‹", page > 0, page - 1)
        this.addChild(left)

        const counter = text(`${page + 1} / ${pages}`, { role: "compactCaption", color: TEXT_DIM })
        counter.x = 24
        counter.y = Math.round((BUTTON_SIZES.extraSmall.height - counter.height) / 2)
        this.addChild(counter)

        const right = arrow("›", page < pages - 1, page + 1)
        right.x = 28 + counter.width
        this.addChild(right)

        this.boxWidth = right.x + 18
    }
}
